import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle2, FileSignature } from "lucide-react";
import SignaturePad from "@/components/SignaturePad";

const REQUIRED_BOXES = [
  { k: "read_and_agree", l: "I have read and agree to this Host Agreement." },
  { k: "commission_qualified", l: "I understand commission is paid only on qualified tracked sales unless otherwise approved." },
  { k: "ownership", l: "I understand artwork remains property of Simple Truths Club unless purchased or otherwise stated." },
  { k: "display_proper", l: "I agree to display artwork and QR signage properly." },
];

export default function PartnerAgreement() {
  const { id } = useParams();
  const nav = useNavigate();
  const [a, setA] = useState(null);
  const [signerName, setSignerName] = useState("");
  const [signerTitle, setSignerTitle] = useState("");
  const [sig, setSig] = useState("");
  const [ack, setAck] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    http.get(`/partner/agreements/${id}`)
      .then(({ data }) => {
        setA(data);
        setSignerName(data.vars?.host_name || "");
      })
      .catch((err) => {
        // 401 handled by global interceptor (redirect to login?next=…)
        if (err.response?.status !== 401) {
          toast.error("Agreement not found.");
          nav("/partner");
        }
      });
  }, [id, nav]);

  if (!a) return <Layout><div className="py-32 text-center text-[#8a857d]"><Loader2 className="animate-spin inline-block" size={16} /></div></Layout>;

  const isSigned = a.status === "signed";

  const canSubmit =
    !isSigned && signerName.trim().length > 1 && sig &&
    REQUIRED_BOXES.every(b => ack[b.k]);

  const submit = async () => {
    if (!canSubmit) { toast.error("Complete all fields, check every box, and draw your signature."); return; }
    setBusy(true);
    try {
      await http.post(`/partner/agreements/${id}/sign`, {
        signer_name: signerName,
        signer_title: signerTitle,
        signature_data_url: sig,
        acknowledgments: ack,
      });
      toast.success("Agreement signed. Welcome aboard.");
      nav("/partner");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't sign.");
    } finally { setBusy(false); }
  };

  return (
    <Layout>
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-3 inline-flex items-center gap-2">
            <FileSignature size={14} /> Host Agreement
          </div>
          <h1 className="font-display text-4xl text-[#F4F1EA] mb-2">
            {isSigned ? "Your signed agreement" : "Sign to activate your wall"}
          </h1>
          {isSigned && (
            <p className="text-emerald-300 mb-6 inline-flex items-center gap-2">
              <CheckCircle2 size={16} /> Signed {new Date(a.signed_at).toLocaleDateString()} by {a.signer_name}
            </p>
          )}

          {/* Agreement body */}
          <div className="border border-white/10 bg-[#0a0a0a] p-6 max-h-[420px] overflow-y-auto" data-testid="agreement-body">
            <pre className="whitespace-pre-wrap font-body text-[13px] text-[#E8E4DC]">
              {a.content_rendered}
            </pre>
          </div>

          {!isSigned && (
            <div className="mt-8 space-y-5">
              <div className="space-y-2">
                {REQUIRED_BOXES.map(b => (
                  <label key={b.k} className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={!!ack[b.k]}
                      onChange={(e) => setAck({ ...ack, [b.k]: e.target.checked })}
                      data-testid={`ack-${b.k}`}
                      className="mt-1 accent-[#C9A055]" />
                    <span className="text-[14px] text-[#E8E4DC]">{b.l}</span>
                  </label>
                ))}
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <label>
                  <div className="font-ui text-[9px] tracking-[0.22em] uppercase text-[#8a857d] mb-1">Typed full legal name *</div>
                  <input required value={signerName} onChange={(e) => setSignerName(e.target.value)}
                    data-testid="sig-name" className="stc-input w-full" />
                </label>
                <label>
                  <div className="font-ui text-[9px] tracking-[0.22em] uppercase text-[#8a857d] mb-1">Title (optional)</div>
                  <input value={signerTitle} onChange={(e) => setSignerTitle(e.target.value)}
                    data-testid="sig-title" className="stc-input w-full" />
                </label>
              </div>

              <div>
                <div className="font-ui text-[9px] tracking-[0.22em] uppercase text-[#8a857d] mb-1">Draw your signature *</div>
                <SignaturePad onChange={setSig} />
              </div>

              <button onClick={submit} disabled={!canSubmit || busy}
                data-testid="sign-submit"
                className="btn-gold w-full py-4 disabled:opacity-40 inline-flex items-center justify-center gap-2">
                {busy ? <Loader2 className="animate-spin" size={14} /> : "Sign Agreement"}
              </button>
              <p className="text-[11px] text-[#8a857d] text-center">
                By submitting, you confirm this electronic acceptance is legally
                binding. We&apos;ll record your IP, timestamp, and user-agent on the
                signed document.
              </p>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
